import {
  HttpHandler,
  HttpRequest,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, switchMap, tap, retryWhen, concatMap, delay } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { LoadingDialogService } from 'src/app/shared/loading/loading-dialog.service';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/shared/services/auth.service';
import { StatusCode } from 'src/app/core/utils/constants/status';
import { excludeLoadingUrl } from './loading-config/loading-config';
import { SnackBarService } from '../../../shared/snack-bar/services/snack-bar.service';
import { basicSnackBarConfig } from '../../../utils/default-configs/configuration';
import { ValidationDialogService } from '../validations-dialog/validation-dialog.service';
import { ConfigService } from 'src/app/shared/services/config.service';
import { createConfig } from '../../../utils/default-configs/configuration';
import { allconfig } from 'src/app/utils/constants/constant';
import { detect, replace } from 'out-of-character';
@Injectable()
export class HttpLoadingInterceptor implements HttpInterceptor {
  requestClone: any;

  constructor(
    private loadingDialogService: LoadingDialogService,
    private snackBarService: SnackBarService,
    private authService: AuthService,
    private validationDialogService: ValidationDialogService,
    private configService: ConfigService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip interceptor for translation file requests (static assets)
    if (request.url.includes('/assets/i18n/') || request.url.includes('assets/i18n/')) {
      return next.handle(request);
    }
    if (!request?.body?.search) {
      this.showLoading(request);
    }

    const reqHeaders: any = {};
    const apimHeaderKey = environment.apimHeader;
    reqHeaders[apimHeaderKey] = environment.apimKey;
    this.requestClone = request;
    this.requestClone = this.addTokenHeader(
      request,
      this.authService.getAuthToken(),
      this.getCurrentUrl(request)
    );

    return next.handle(this.requestClone).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          if (event?.body?.body?.cacheActive === false) {
            sessionStorage.setItem('cacheActive', 'false');
          }
          if (event?.body) {
            let bodyStr = JSON.stringify(event?.body);
            //let hasEsp = detect(bodyStr);
            let body = replace(bodyStr);
            event.clone({ body: JSON.parse(body) });
          }

          this.loadingDialogService.hideLoading();
          this.snackBarService.dismissSnackbar();
        }
      }),
      retryWhen(error => this.handleNotfound(error, 1)),
      catchError(errordata => {
        this.loadingDialogService.hideLoading();
        this.snackBarService.dismissSnackbar();
        if (errordata.status === StatusCode.UNAUTHORIZED) {
          this.authService.logOut();
        }
        if (
          errordata.status === StatusCode.FORBIDDEN &&
          request.url.split('/').pop() !== 'getaccesstoken' &&
          request.url.split('/').pop() !== 'postallist'
        ) {
          return this.handleRefreshToken(request, next);
        }
        if (errordata instanceof HttpErrorResponse) {
          this.validationDialogService.handleError(errordata);
        }
        return throwError(errordata);
      })
    );
  }

  handleCacheActiveStatus() {
    return sessionStorage.getItem('cacheActive');
  }
  handleSid() {
    return sessionStorage.getItem('sid');
  }
  handleBid() {
    return sessionStorage.getItem('bid');
  }

  handleProductType() {
    return sessionStorage.getItem('loose-bbx') === 'No' ? 'bbx' : 'lbx';
  }

  handleNotfound(error: Observable<unknown>, retryCount: number) {
    return error.pipe(
      concatMap((checkError: HttpErrorResponse, count) => {
        if (checkError?.status === StatusCode.NOT_FOUND && count <= retryCount) {
          return of(checkError);
        }
        return throwError(checkError);
      }),
      delay(1000)
    );
  }

  handleRefreshToken(request: HttpRequest<any>, next: HttpHandler) {
    return this.authService.refreshToken().pipe(
      switchMap((data: any) => {
        this.refreshAllConfigurations();
        this.authService.saveTokens(data.token);
        return next.handle(this.addTokenHeader(request, data.token, this.getCurrentUrl(request)));
      }),
      catchError(refreshError => {
        this.authService.logOut();
        return throwError(refreshError);
      })
    );
  }

  addTokenHeader(request: HttpRequest<any>, token: any, path) {
    const apimHeaderKey = environment.apimHeader;
    const appUrl = window.location.origin;
    const reqHeaders: any = {
      'Strict-Transport-Security': 'max-age=31536000; includeSubdomains',
      'X-Frame-Options': 'deny',
      'X-Content-Type-Options': 'nosniff',
      'X-Xss-Protection': '1; mode=block',
      'Access-Control-Allow-Origin': [environment.passhost, appUrl],
      'Referrer-Policy': 'no-referrer',
      'Cache-Control': 'no-store, max-age=0',
      Pragma: 'no-cache',
      'Content-Security-Policy':
        "default-src 'self'; object-src 'none'; frame-ancestors 'none'; 'upgrade-insecure-requests'; 'block-all-mixed-content'",
      productType: this.handleProductType(),
      locale: localStorage.getItem('locale'),
    };

    const tokenConfigs = {
      getaccesstoken: () => {
        reqHeaders[apimHeaderKey] = environment.apimKey;
        reqHeaders.currentuserrole = this.authService.getUserRole();
        productType: this.handleProductType();
        reqHeaders.Authorization = `Bearer ${this.authService.getRefreshToken()}`;
        return (this.requestClone = request.clone({
          setHeaders: reqHeaders,
        }));
      },
      default: () => {
        reqHeaders[apimHeaderKey] = environment.apimKey;
        reqHeaders.currentuserrole = this.authService.getUserRole();
        reqHeaders.currentcustomerID = this.authService.getCustomerId();
        reqHeaders.sid = this.handleSid();
        reqHeaders.bid = this.handleBid();
        reqHeaders.cacheActive = this.handleCacheActiveStatus();
        productType: this.handleProductType();
        reqHeaders.Authorization = `Bearer ${token}`;
        return (this.requestClone = request.clone({
          setHeaders: reqHeaders,
        }));
      },
      login: () => {
        reqHeaders[apimHeaderKey] = environment.apimKey;
        return (this.requestClone = request.clone({
          setHeaders: reqHeaders,
        }));
      },

      loginBackup: () => {
        reqHeaders[apimHeaderKey] = environment.apimKey;
        return (this.requestClone = request.clone({
          setHeaders: reqHeaders,
        }));
      },

      forgotPassword: () => {
        reqHeaders[apimHeaderKey] = environment.apimKey;
        return (this.requestClone = request.clone({
          setHeaders: reqHeaders,
        }));
      },

      resetPassword: () => {
        reqHeaders[apimHeaderKey] = environment.apimKey;
        reqHeaders.Authorization = `Bearer ${token}`;
        sessionStorage.clear();
        return (this.requestClone = request.clone({
          setHeaders: reqHeaders,
        }));
      },
      deleteConfiguration: () => {
        reqHeaders[apimHeaderKey] = environment.apimKey;
        reqHeaders.currentuserrole = this.authService.getUserRole();
        reqHeaders.currentcustomerID = this.authService.getCustomerId();
        productType: this.handleProductType();
        reqHeaders.Authorization = `Bearer ${token}`;
        reqHeaders.cacheActive = this.handleCacheActiveStatus();
        return (this.requestClone = request.clone({
          setHeaders: reqHeaders,
        }));
      },
      addconfiguration: () => {
        reqHeaders[apimHeaderKey] = environment.apimKey;
        reqHeaders.currentuserrole = this.authService.getUserRole();
        reqHeaders.currentcustomerID = this.authService.getCustomerId();
        productType: this.handleProductType();
        reqHeaders.Authorization = `Bearer ${token}`;
        reqHeaders.cacheActive = this.handleCacheActiveStatus();
        return (this.requestClone = request.clone({
          setHeaders: reqHeaders,
        }));
      },
      getconfiguration: () => {
        reqHeaders[apimHeaderKey] = environment.apimKey;
        reqHeaders.currentuserrole = this.authService.getUserRole();
        reqHeaders.currentcustomerID = this.authService.getCustomerId();
        productType: this.handleProductType();
        reqHeaders.Authorization = `Bearer ${token}`;
        reqHeaders.cacheActive = this.handleCacheActiveStatus();
        return (this.requestClone = request.clone({
          setHeaders: reqHeaders,
        }));
      },
    };
    return (tokenConfigs[path] || tokenConfigs.default)();
  }

  getCurrentUrl(request) {
    return request.url.split('/').pop();
  }

  showLoading(request) {
    const displayMsg =
      this.getCurrentUrl(request) === excludeLoadingUrl.renderBatchLabel
        ? basicSnackBarConfig.shipmentListGenerateLabel
        : basicSnackBarConfig.shipmentListViewLabel;
    return this.getCurrentUrl(request) === excludeLoadingUrl.renderBatchLabel ||
      this.getCurrentUrl(request) === excludeLoadingUrl.fetchBatchLabel
      ? this.snackBarService.createSnackBar(
          displayMsg?.message,
          displayMsg?.horizontalPosition,
          displayMsg?.verticalPosition,
          displayMsg?.duration,
          true
        )
      : this.loadingDialogService.showLoading();
  }

  refreshAllConfigurations() {
    this.configService
      .getConfig(createConfig(allconfig))
      .pipe(
        concatMap(response => {
          const putConfigObservables = response.map(item => {
            if (Object.keys(item).length !== 0) {
              return this.configService.putConfig(item.config_id, item.data);
            } else {
              return of(null);
            }
          });
          return forkJoin(putConfigObservables);
        })
      )
      .subscribe(() => {
        console.log(`test refresh`);
      });
  }
}
